import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';

const enquiryCards = [
    { title: '60-Minute Focused Sessions', description: 'A structured, time-efficient advisory call with a practitioner who has directly relevant experience - no generic advice, no junior consultants.', icon: <><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></> },
    { title: 'Rigorously Vetted Experts', description: 'Every expert on our network has passed a structured assessment - credential verification, capability interview, reference checks, and Community of Excellence review. Less than 20% of applicants are accepted.', icon: <><path d="M9 12l2 2 4-4"/><path d="M21 12c0 5.591-3.824 10.29-9 11.622C6.824 22.29 3 17.591 3 12c0-1.042.133-2.052.382-3.016A11.955 11.955 0 0012 5.944c2.996 0 5.736 1.1 7.618 3.04"/></> },
    { title: 'No Long-Term Commitment', description: "Enquiry calls stand alone - no retainer, no ongoing contract required. Use them when you need a specific answer or a sanity-check from someone who's been there before.", icon: <path d="M13 10V3L4 14h7v7l9-11h-7z"/> },
    { title: 'Matched to Your Challenge', description: 'We hand-pick your expert from our 3,500+ network based on your specific brief - not keyword search, but curated human judgement to ensure the best possible fit.', icon: <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/> },
    { title: 'Available via the Client Desk', description: "Submit your enquiry directly through the HiveMind Client Desk portal. Our team responds to every request within 4 business hours, and you're typically matched within 48 hours.", icon: <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2"/> },
    { title: 'Only Pay for Value', description: "Our commercial model is built on outcomes. If the call doesn't deliver genuine value, you don't pay. We are invested in every conversation delivering a real result for you.", icon: <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8V7m0 9v1m9-5a9 9 0 11-18 0 9 9 0 0118 0z"/> }
];

const benefits = [
    { title: 'Clarity', description: 'Get expert perspective on a specific challenge in hours, not weeks. Walk away with clear direction - not more questions.', icon: <path d="M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/> },
    { title: 'Speed', description: 'No lengthy procurement, no drawn-out scoping exercises. Submit your brief today and be speaking to the right expert within 48 hours.', icon: <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/> },
    { title: 'Confidence', description: 'Speak directly to someone who has done it before - a practitioner with hands-on experience in your exact domain and challenge type.', icon: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/> },
    { title: 'Value', description: 'Only pay if the call delivers genuine value. Our outcome-based model means we are invested in every conversation producing a real result - not just filling time.', icon: <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8V7m0 9v1m9-5a9 9 0 11-18 0 9 9 0 0118 0z"/> }
];

const steps = [
    {
        title: 'Submit Your Enquiry',
        icon: <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>,
        desc: "Tell us about your challenge - a two-minute brief is all we need. Submit via the Client Desk portal or email us directly. No lengthy RFPs, no procurement hoops.",
        points: ["Describe your challenge, the decision you're trying to make, and your timeline", 'Submit via the HiveMind Client Desk or email directly', "You'll receive a confirmation and next steps"]
    },
    {
        title: 'Expert Matching',
        icon: <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>,
        desc: 'Our network team hand-picks the best-fit practitioner from our 3,500+ expert community based on your specific brief - curated human judgement, not keyword search.',
        points: ['Expert selected based on domain, seniority, and direct challenge relevance', 'You receive a profile with background, experience, and relevant case examples', "Option to request an alternative if the fit isn't right - no pressure to proceed"]
    },
    {
        title: 'Align & Prepare',
        icon: <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>,
        desc: 'A brief alignment exchange ensures your expert is fully prepared for your challenge. You agree on the session agenda, key questions, and the outcomes you need.',
        points: ['Share background documents, data, or context with your expert ahead of the call', 'Agree on the specific questions and decisions the session should address', 'Confirm session format: structured interview, working session, or advisory review']
    },
    {
        title: 'Your Enquiry Call',
        icon: <path d="M15 10l4.553-2.069A1 1 0 0121 8.845v6.31a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>,
        desc: 'A focused 60-minute session with your matched expert. Direct access to practitioner-level experience - honest, specific, and oriented towards your decision.',
        points: ['60 minutes of direct, senior-level expert time - no account managers in the room', 'Session notes and key takeaways shared after the session', 'Option to record the session for internal sharing']
    },
    {
        title: 'Progress & Next Steps',
        icon: <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>,
        desc: "You confirm the value delivered. If the call met your needs, you're invoiced. If not, you don't pay - it's that simple. Follow-on calls or deeper engagements can be arranged if needed.",
        points: ['Value confirmation - you tell us whether the call delivered', 'Only pay if the session produced genuine insight or direction', 'Option to commission follow-on advisory support or a deeper engagement']
    }
];

const expertise = [
    ['Strategy', '180+ experts', <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>],
    ['Engineering', '420+ experts', <path d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>],
    ['Product', '310+ experts', <path d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"/>],
    ['Data & AI', '380+ experts', <path d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"/>],
    ['Design & UX', '220+ experts', <path d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"/>],
    ['Programme Delivery', '290+ experts', <path d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"/>],
    ['Cyber Security', '160+ experts', <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>],
    ['Cloud & Infra', '200+ experts', <path d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"/>],
    ['Change Management', '240+ experts', <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>],
    ['Finance & CFO', '140+ experts', <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8V7m0 9v1m9-5a9 9 0 11-18 0 9 9 0 0118 0z"/>],
    ['Growth & Marketing', '175+ experts', <path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>],
    ['Operations', '195+ experts', <path d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>]
];

const processStages = [
    'Exploring / early stage',
    'Evaluating options',
    'Planning implementation',
    'In delivery',
    'Troubleshooting'
];

const timeWindowOptions = [
    { value: 'early_morning', label: 'Early morning (06:00-09:00)' },
    { value: 'morning', label: 'Morning (09:00-12:00)' },
    { value: 'afternoon', label: 'Afternoon (12:00-17:00)' },
    { value: 'evening', label: 'Evening (17:00-21:00)' }
];

const initialFormData = {
    helpNeed: '',
    keyQuestion: '',
    valuableOutcome: '',
    backgroundContext: '',
    processStage: '',
    expertiseType: '',
    preferredIndustry: '',
    timezone: '',
    preferredDateFrom: '',
    preferredDateTo: '',
    preferredTimeWindows: [],
    urgencyLevel: '',
    additionalNotes: ''
};

function EnquiryCallPage({ onBack, token }) {
    const [showEnquiryModal, setShowEnquiryModal] = useState(false);
    const [formData, setFormData] = useState(initialFormData);
    const [formErrors, setFormErrors] = useState({});
    const [validationSummary, setValidationSummary] = useState('');
    const [submitMessage, setSubmitMessage] = useState('');
    const [submitError, setSubmitError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const successCloseTimerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (successCloseTimerRef.current) {
                clearTimeout(successCloseTimerRef.current);
            }
        };
    }, []);

    const openEnquiryModal = () => {
        setShowEnquiryModal(true);
    };

    const closeEnquiryModal = () => {
        if (successCloseTimerRef.current) {
            clearTimeout(successCloseTimerRef.current);
            successCloseTimerRef.current = null;
        }
        setShowEnquiryModal(false);
        setFormData(initialFormData);
        setFormErrors({});
        setValidationSummary('');
        setSubmitError('');
        setSubmitMessage('');
    };

    const handleFieldChange = (event) => {
        const { name, value } = event.target;

        setFormData((prevState) => ({
            ...prevState,
            [name]: value
        }));

        setFormErrors((prevErrors) => ({
            ...prevErrors,
            [name]: ''
        }));

        setValidationSummary('');
    };

    const handleTimeWindowToggle = (windowValue) => {
        setFormData((prevState) => {
            const alreadySelected = prevState.preferredTimeWindows.includes(windowValue);
            const nextWindows = [...prevState.preferredTimeWindows];

            if (alreadySelected) {
                const filteredWindows = [];
                let i = 0;
                for (i = 0; i < nextWindows.length; i += 1) {
                    if (nextWindows[i] !== windowValue) {
                        filteredWindows.push(nextWindows[i]);
                    }
                }

                return {
                    ...prevState,
                    preferredTimeWindows: filteredWindows
                };
            }

            nextWindows.push(windowValue);
            return {
                ...prevState,
                preferredTimeWindows: nextWindows
            };
        });

        setFormErrors((prevErrors) => ({
            ...prevErrors,
            preferredTimeWindows: ''
        }));

        setValidationSummary('');
    };

    const validateForm = () => {
        const nextErrors = {};

        if (!formData.helpNeed.trim()) {
            nextErrors.helpNeed = 'Please share what you need help with.';
        }

        if (!formData.keyQuestion.trim()) {
            nextErrors.keyQuestion = 'Please add the key question you want answered.';
        }

        if (!formData.valuableOutcome.trim()) {
            nextErrors.valuableOutcome = 'Please define the outcome that would make this call valuable.';
        }

        if (!formData.backgroundContext.trim()) {
            nextErrors.backgroundContext = 'Please provide brief background or context.';
        }

        if (!formData.processStage) {
            nextErrors.processStage = 'Please select where you are in the process.';
        }

        if (!formData.expertiseType.trim()) {
            nextErrors.expertiseType = 'Please describe the expertise you are looking for.';
        }

        if (!formData.preferredIndustry.trim()) {
            nextErrors.preferredIndustry = 'Please enter your preferred industry experience.';
        }

        if (!formData.timezone.trim()) {
            nextErrors.timezone = 'Please enter your preferred geography or timezone.';
        }

        if (!formData.urgencyLevel) {
            nextErrors.urgencyLevel = 'Please choose an urgency level.';
        }

        if (!formData.preferredDateFrom || !formData.preferredDateTo) {
            nextErrors.preferredTiming = 'Please add both a preferred start and end date.';
        }

        if (formData.preferredDateFrom && formData.preferredDateTo && formData.preferredDateFrom > formData.preferredDateTo) {
            nextErrors.preferredTiming = 'Preferred start date cannot be later than end date.';
        }

        if (!formData.preferredTimeWindows.length) {
            nextErrors.preferredTimeWindows = 'Please select at least one preferred time window.';
        }

        setFormErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleFormSubmit = async (event) => {
        event.preventDefault();
        setSubmitMessage('');
        setSubmitError('');
        setValidationSummary('');

        const isValid = validateForm();

        if (!isValid) {
            setValidationSummary('Please correct the highlighted fields before submitting.');
            return;
        }

        if (!token) {
            setSubmitError('You must be logged in to submit this enquiry.');
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await fetch('/api/enquiry-calls/create.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + token
                },
                body: JSON.stringify({
                    helpNeed: formData.helpNeed,
                    keyQuestion: formData.keyQuestion,
                    valuableOutcome: formData.valuableOutcome,
                    backgroundContext: formData.backgroundContext,
                    processStage: formData.processStage,
                    expertiseType: formData.expertiseType,
                    preferredIndustry: formData.preferredIndustry,
                    timezone: formData.timezone,
                    preferredDateFrom: formData.preferredDateFrom,
                    preferredDateTo: formData.preferredDateTo,
                    preferredTimeWindows: formData.preferredTimeWindows,
                    urgencyLevel: formData.urgencyLevel,
                    additionalNotes: formData.additionalNotes
                })
            });

            const data = await response.json();
            if (!response.ok) {
                if (data && data.errors && typeof data.errors === 'object') {
                    setFormErrors((prevErrors) => ({
                        ...prevErrors,
                        ...data.errors
                    }));
                }

                const errorMessage = data && data.error ? data.error : 'Unable to submit enquiry. Please try again.';
                setSubmitError(errorMessage);
                return;
            }

            setSubmitMessage('Thanks - your enquiry has been submitted successfully.');
            setFormData(initialFormData);
            setFormErrors({});
            setValidationSummary('');
            successCloseTimerRef.current = setTimeout(() => {
                closeEnquiryModal();
            }, 1500);
        } catch (submitErr) {
            setSubmitError('Unable to submit enquiry right now. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="enquiry-page enquiry-page--lightonly">
            <div className="hero-bg">
                <div className="hero-orb orb-1"></div>
                <div className="hero-orb orb-2"></div>
            </div>

            <main className="enquiry-main" id="enquiry-top">
                <div className="enquiry-back-bar">
                    <Button variant="outline-secondary" size="sm" onClick={onBack}>Back to Dashboard</Button>
                </div>
                <div className="page-header">
                    <div className="section-label">EXPERT ENQUIRY CALLS</div>
                    <h1>On-Demand Expert Insight from <span className="gradient-text">3,500+</span><br />World-Class Practitioners</h1>
                    <p>An invitation-only community of independent practitioners - rigorously vetted, deeply experienced, and ready to answer your most critical business questions.</p>
                </div>
                <div className="stats-banner">
                    <div className="stats-inner">
                        <div><div className="stat-num">3,500+</div><div className="stat-label">Vetted Experts</div></div>
                        <div><div className="stat-num">40+</div><div className="stat-label">Disciplines</div></div>
                        <div><div className="stat-num">48h</div><div className="stat-label">Typical Match Time</div></div>
                        <div><div className="stat-num">&lt;20%</div><div className="stat-label">Acceptance Rate</div></div>
                    </div>
                </div>

                <div className="section">
                    <div className="section-label">What Are Enquiry Calls</div>
                    <div className="section-title">Expert insight, exactly when you need it</div>
                    <p className="section-sub">Focused advisory sessions that connect you directly with a vetted subject matter expert - designed to answer a specific question or challenge quickly, without long-term commitment.</p>
                    <div className="enquiry-grid">
                        {enquiryCards.map((item) => (
                            <div className="enquiry-card" key={item.title}>
                                <div className="enquiry-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">{item.icon}</svg></div>
                                <h3>{item.title}</h3>
                                <p>{item.description}</p>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="section">
                    <div className="section-label">Client Benefits</div>
                    <div className="section-title">Why clients choose Enquiry Calls</div>
                    <p className="section-sub">Get the clarity, speed, and confidence you need to make better decisions - backed by practitioners who&apos;ve already solved your problem before.</p>
                    <div className="benefits-grid">
                        {benefits.map((item) => (
                            <div className="benefit-card" key={item.title}>
                                <div className="benefit-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{item.icon}</svg></div>
                                <h3>{item.title}</h3>
                                <p>{item.description}</p>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="section timeline-header">
                    <div className="section-label">The Process</div>
                    <div className="section-title">From brief to expert, in five steps</div>
                    <p className="section-sub">Here&apos;s exactly what to expect at every stage.</p>
                </div>
                <div className="timeline-section">
                    <div className="timeline-track">
                        {steps.map((step, idx) => (
                            <div className="step" key={step.title}>
                                <div className="step-left">
                                    <div className={`step-num ${idx === 0 ? 'active-step' : ''}`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{step.icon}</svg></div>
                                    <span className="step-badge">Step {idx + 1}</span>
                                </div>
                                <div className="step-content">
                                    <div className="step-header"><span className="step-title">{step.title}</span></div>
                                    <p className="step-desc">{step.desc}</p>
                                    <ul className="step-bullets">{step.points.map((p) => <li key={p}>{p}</li>)}</ul>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="section">
                    <div className="section-label">Expertise Areas</div>
                    <div className="section-title">Every capability. One network.</div>
                    <p className="section-sub">From day-one strategy to final-mile delivery - our 3,500+ experts cover the full spectrum of modern business transformation.</p>
                    <div className="expertise-grid">
                        {expertise.map(([name, count, icon]) => (
                            <div className="expertise-card" key={name}>
                                <div className="exp-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">{icon}</svg></div>
                                <div className="exp-name">{name}</div>
                                <div className="exp-count">{count}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="cta-block">
                    <div className="cta-strip">
                        <div className="section-label cta-label">Get Started</div>
                        <h2>Ready to book your Enquiry Call?</h2>
                        <p>Submit your brief today and be speaking to the right expert within 48 hours. No commitment, no procurement hoops - just the right insight at the right time.</p>
                        <button type="button" className="cta-btn" onClick={openEnquiryModal}>Submit an Enquiry</button>
                    </div>
                </div>
            </main>

            <footer className="enquiry-footer">
                <div className="footer-inner">
                    <div className="footer-logo"><img src="https://clever-slate-7bkv.here.now/assets/logo.png" alt="HiveMind Network" /></div>
                    <p>© 2025 HiveMind Network Ltd. Expert Enquiry Calls.</p>
                </div>
            </footer>

            <Modal
                show={showEnquiryModal}
                onHide={closeEnquiryModal}
                centered
                size="lg"
                className="enquiry-form-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Submit an Enquiry Call</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="enquiry-form-intro">
                        Enquiry calls give you fast access to HiveMind&apos;s network of expert practitioners, matched to your specific challenge within 48 hours.
                    </p>
                    <Form onSubmit={handleFormSubmit} noValidate>
                        <div className="enquiry-form-group">
                            <h4>Core Fields</h4>
                            <Form.Group className="mb-3" controlId="helpNeed">
                                <Form.Label>What do you need help with?</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="helpNeed"
                                    value={formData.helpNeed}
                                    onChange={handleFieldChange}
                                    placeholder="Short description of the challenge"
                                    isInvalid={Boolean(formErrors.helpNeed)}
                                />
                                <Form.Control.Feedback type="invalid">{formErrors.helpNeed}</Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3" controlId="keyQuestion">
                                <Form.Label>What is the key question you want answered?</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    name="keyQuestion"
                                    value={formData.keyQuestion}
                                    onChange={handleFieldChange}
                                    placeholder="State the one question that matters most"
                                    isInvalid={Boolean(formErrors.keyQuestion)}
                                />
                                <Form.Control.Feedback type="invalid">{formErrors.keyQuestion}</Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3" controlId="valuableOutcome">
                                <Form.Label>What outcome would make this call valuable for you?</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    name="valuableOutcome"
                                    value={formData.valuableOutcome}
                                    onChange={handleFieldChange}
                                    placeholder="Define success for this call"
                                    isInvalid={Boolean(formErrors.valuableOutcome)}
                                />
                                <Form.Control.Feedback type="invalid">{formErrors.valuableOutcome}</Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3" controlId="backgroundContext">
                                <Form.Label>Brief background/context</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="backgroundContext"
                                    value={formData.backgroundContext}
                                    onChange={handleFieldChange}
                                    placeholder="Any useful context, constraints, or history"
                                    isInvalid={Boolean(formErrors.backgroundContext)}
                                />
                                <Form.Control.Feedback type="invalid">{formErrors.backgroundContext}</Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-0" controlId="processStage">
                                <Form.Label>Where are you currently in this process?</Form.Label>
                                <Form.Select
                                    name="processStage"
                                    value={formData.processStage}
                                    onChange={handleFieldChange}
                                    isInvalid={Boolean(formErrors.processStage)}
                                >
                                    <option value="">Select a stage</option>
                                    {processStages.map((stage) => (
                                        <option key={stage} value={stage}>{stage}</option>
                                    ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">{formErrors.processStage}</Form.Control.Feedback>
                            </Form.Group>
                        </div>

                        <div className="enquiry-form-group">
                            <h4>Expertise Matching</h4>
                            <Form.Group className="mb-3" controlId="expertiseType">
                                <Form.Label>What type of expertise are you looking for?</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="expertiseType"
                                    value={formData.expertiseType}
                                    onChange={handleFieldChange}
                                    placeholder="Example: pricing strategy, data platform migration"
                                    isInvalid={Boolean(formErrors.expertiseType)}
                                />
                                <Form.Control.Feedback type="invalid">{formErrors.expertiseType}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-0" controlId="preferredIndustry">
                                <Form.Label>Preferred industry experience (if any)</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="preferredIndustry"
                                    value={formData.preferredIndustry}
                                    onChange={handleFieldChange}
                                    placeholder="Example: healthcare, fintech, manufacturing"
                                    isInvalid={Boolean(formErrors.preferredIndustry)}
                                />
                                <Form.Control.Feedback type="invalid">{formErrors.preferredIndustry}</Form.Control.Feedback>
                            </Form.Group>
                        </div>

                        <div className="enquiry-form-group">
                            <h4>Logistics</h4>
                            <div className="timing-grid">
                                <Form.Group className="mb-3" controlId="preferredDateFrom">
                                    <Form.Label>Preferred timing: from date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="preferredDateFrom"
                                        value={formData.preferredDateFrom}
                                        onChange={handleFieldChange}
                                        isInvalid={Boolean(formErrors.preferredTiming)}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3" controlId="preferredDateTo">
                                    <Form.Label>Preferred timing: to date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="preferredDateTo"
                                        value={formData.preferredDateTo}
                                        onChange={handleFieldChange}
                                        isInvalid={Boolean(formErrors.preferredTiming)}
                                    />
                                </Form.Group>
                            </div>
                            {formErrors.preferredTiming && <div className="field-error-text">{formErrors.preferredTiming}</div>}

                            <Form.Group className="mb-3" controlId="preferredTimeWindows">
                                <Form.Label>Preferred time windows</Form.Label>
                                <div className="checkbox-grid">
                                    {timeWindowOptions.map((windowOption) => (
                                        <Form.Check
                                            key={windowOption.value}
                                            id={`window-${windowOption.value}`}
                                            type="checkbox"
                                            label={windowOption.label}
                                            checked={formData.preferredTimeWindows.includes(windowOption.value)}
                                            onChange={() => handleTimeWindowToggle(windowOption.value)}
                                        />
                                    ))}
                                </div>
                                {formErrors.preferredTimeWindows && <div className="field-error-text">{formErrors.preferredTimeWindows}</div>}
                            </Form.Group>

                            <Form.Group className="mb-3" controlId="timezone">
                                <Form.Label>Preferred geography/timezone (if relevant)</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="timezone"
                                    value={formData.timezone}
                                    onChange={handleFieldChange}
                                    placeholder="Example: UK timezone, US East Coast, CET"
                                    isInvalid={Boolean(formErrors.timezone)}
                                />
                                <Form.Control.Feedback type="invalid">{formErrors.timezone}</Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-0" controlId="urgencyLevel">
                                <Form.Label>Urgency level</Form.Label>
                                <div className="urgency-list">
                                    <Form.Check
                                        type="radio"
                                        name="urgencyLevel"
                                        id="urgency-low"
                                        value="Low (next 1-2 weeks)"
                                        label="Low (next 1-2 weeks)"
                                        checked={formData.urgencyLevel === 'Low (next 1-2 weeks)'}
                                        onChange={handleFieldChange}
                                    />
                                    <Form.Check
                                        type="radio"
                                        name="urgencyLevel"
                                        id="urgency-medium"
                                        value="Medium (within a week)"
                                        label="Medium (within a week)"
                                        checked={formData.urgencyLevel === 'Medium (within a week)'}
                                        onChange={handleFieldChange}
                                    />
                                    <Form.Check
                                        type="radio"
                                        name="urgencyLevel"
                                        id="urgency-high"
                                        value="High (within 48 hours)"
                                        label="High (within 48 hours)"
                                        checked={formData.urgencyLevel === 'High (within 48 hours)'}
                                        onChange={handleFieldChange}
                                    />
                                </div>
                                {formErrors.urgencyLevel && <div className="field-error-text">{formErrors.urgencyLevel}</div>}
                            </Form.Group>
                        </div>

                        <div className="enquiry-form-group">
                            <h4>Optional</h4>
                            <Form.Group className="mb-0" controlId="additionalNotes">
                                <Form.Label>Anything else we should know to find the best expert?</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="additionalNotes"
                                    value={formData.additionalNotes}
                                    onChange={handleFieldChange}
                                    placeholder="Any extra details to help us match the right expert"
                                />
                            </Form.Group>
                        </div>

                        {validationSummary && (
                            <Alert variant="danger" className="mb-3 enquiry-validation-summary-alert">
                                {validationSummary}
                            </Alert>
                        )}

                        {submitError && (
                            <Alert variant="danger" className="mb-3">
                                {submitError}
                            </Alert>
                        )}

                        {submitMessage && (
                            <Alert variant="success" className="mb-3">
                                {submitMessage}
                            </Alert>
                        )}

                        <div className="form-actions">
                            <Button variant="outline-secondary" type="button" onClick={closeEnquiryModal}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" disabled={isSubmitting}>
                                {isSubmitting ? 'Submitting...' : 'Submit'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
}

export default EnquiryCallPage;
